/**
 * Converter for Dirks Bio-Kiste (docs/18-vorratsbuch.md, Eingänge): invoice („Rechnung“) and order
 * confirmation („Bestellbestätigung“) as PDF, read by pdf.js into positioned text (`pdfRows`).
 *
 * The table: Artikel, Herkunft (invoice) or Lieferzyklus (order), Menge, Einheit, Einzelpreis, USt,
 * Rabatt (invoice), Gesamt. Besides the goods it holds rows that are none:
 *   - a box or an order as a heading over its goods („Single-Kiste M“, „Individualbestellung“), the
 *     goods indented below it; in the order with an estimated price („ca. 28,00 €“) and no total;
 *   - the delivery cycle on a row of its own („alle 2 Wochen“, invoice only);
 *   - a substitution („Kopfsalat grün wurde gegen Chinakohl ausgetauscht“), below the line it is about
 *     or on the next page;
 *   - sums, the delivery fee and the page footer.
 * A good is a row with quantity, unit and total. Everything above the table's heading (the box
 * „Nachpackartikel“, the addresses) is left out.
 */
import { cents, germanDate, germanNumber, pdfRows } from "./pdf-lines.ts";
import type { PdfCell, PdfItem, PdfRow } from "./pdf-lines.ts";
import { quantityOf } from "./inbound.ts";
import type { Inbound, InboundLine } from "./inbound.ts";

/** Is this a document of Dirks Bio-Kiste? */
export function isBiokiste(rows: readonly PdfRow[]): boolean {
  return rows.some((r) => /dirksbiokiste\.de|Dirks Bio-Kiste/i.test(r.text));
}

const MONEY = /^(ca\. )?-?\d{1,3}(\.\d{3})*,\d{2}$/;
const PERCENT = /^\d+(,\d+)? %$/;
const ORIGIN = /^([A-Z]{1,3}) - (.+)$/;
const CYCLE = /^(alle \d+ Wochen|wöchentlich|einmalig|alle \d+ Tage)$/i;
const SUBSTITUTE = /^(.+?) wurde gegen (.+?) ausgetauscht\.?$/;
const UNIT = /^(Stück|Stk\.?|kg|g|l|ml|Bund|Schale|Glas|Packung|Pck\.?|Netz|Beutel|Dose|Flasche)$/;

interface Columns {
  /** Left edge of the second column (Herkunft or Lieferzyklus): what is left of it is the name. */
  second: number;
}

/** Money in a row: a number cell followed by a cell „€“ (or with „€“ in it), left to right. */
function money(cells: readonly PdfCell[]): { x: number; cents: number; estimate: boolean }[] {
  const out: { x: number; cents: number; estimate: boolean }[] = [];
  cells.forEach((c, i) => {
    const t = c.text.replace(/\s*€$/, "");
    const euro = c.text.endsWith("€") || cells[i + 1]?.text === "€";
    if (euro && MONEY.test(t)) out.push({ x: c.x, cents: cents(t.replace(/^ca\. /, ""))!, estimate: t.startsWith("ca.") });
  });
  return out;
}

/** The last amount of money in the row: totals stand at the right. */
const lastMoney = (r: PdfRow) => money(r.cells).at(-1)?.cents;

interface Parsed {
  name: string;
  indent: number;
  origin?: string;
  label?: string;
  qty?: number;
  unit?: string;
  money: ReturnType<typeof money>;
  percents: number[];
}

function parseRow(r: PdfRow, cols: Columns): Parsed {
  const nameCells = r.cells.filter((c) => c.x < cols.second - 4);
  const rest = r.cells.filter((c) => c.x >= cols.second - 4);
  const p: Parsed = { name: nameCells.map((c) => c.text).join(" ").trim(), indent: nameCells[0]?.x ?? Infinity, money: money(rest), percents: [] };
  for (let i = 0; i < rest.length; i++) {
    const t = rest[i]!.text;
    const origin = ORIGIN.exec(t);
    if (origin) [p.origin, p.label] = [origin[1], origin[2]];
    else if (PERCENT.test(t)) p.percents.push(germanNumber(t.replace(" %", ""))!);
    else if (p.qty === undefined && germanNumber(t) !== null && UNIT.test(rest[i + 1]?.text ?? "")) [p.qty, p.unit] = [germanNumber(t)!, rest[i + 1]!.text];
    else if (p.qty === undefined) {
      // Quantity and unit in one cell: „1,00 Stück“.
      const m = /^(\d+(?:,\d+)?) (\S+)$/.exec(t);
      if (m && UNIT.test(m[2]!)) [p.qty, p.unit] = [germanNumber(m[1]!)!, m[2]!];
    }
  }
  return p;
}

/** The Eingangsbeleg of an invoice or order confirmation of Dirks Bio-Kiste. */
export function parseBiokiste(items: readonly PdfItem[]): Inbound {
  const rows = pdfRows(items);
  const all = rows.map((r) => r.text).join("\n");
  const doc = /(Rechnung|Lieferschein|Bestellbestätigung) Nr\.? ?([A-Z0-9-]+)/.exec(all);
  const kind = doc?.[1] === "Bestellbestätigung" ? "order" : "delivery";
  const ref = doc?.[2];
  const notes: string[] = [];
  const lines: InboundLine[] = [];
  const substitutions: { from: string; to: string }[] = [];
  let fee: number | undefined;
  let goods: number | undefined;
  let total: number | undefined;
  let issued: string | undefined;

  let cols: Columns | null = null;
  let page = 0;
  /** The heading the current goods stand under, with its indent. */
  let group: { name: string; indent: number } | null = null;
  for (const r of rows) {
    if (r.page !== page) [page, cols] = [r.page, null];
    if (!issued && /^Mauer, \d/.test(r.cells.at(-1)?.text ?? "")) issued = germanDate(r.cells.at(-1)!.text) ?? undefined;
    if (r.cells[0]?.text === "Artikel" && /Menge/.test(r.text)) {
      const second = r.cells[1];
      cols = { second: second ? second.x : 200 };
      continue;
    }
    // Sums and fee may stand on a page without the table's heading.
    if (/Liefergebühr/.test(r.text)) {
      fee = lastMoney(r);
      continue;
    }
    if (/^Gesamt (Brutto-)?Warenwert/.test(r.text)) {
      goods = lastMoney(r);
      continue;
    }
    if (/^(Gesamtsumme|zu zahlender Betrag)/.test(r.text)) {
      total = lastMoney(r) ?? total;
      continue;
    }
    if (!cols) continue;
    if (/^Seite \d+ von \d+/.test(r.text)) {
      cols = null;
      continue;
    }
    const sub = SUBSTITUTE.exec(r.text);
    if (sub) {
      substitutions.push({ from: sub[1]!, to: sub[2]! });
      continue;
    }
    if (CYCLE.test(r.text)) continue;
    // The row of the VAT sums below the goods: „USt Netto USt-Betrag Brutto“ and its figures.
    if (r.cells[0]!.x >= cols.second - 4) continue;
    const p = parseRow(r, cols);
    const totalMoney = p.money.filter((m) => !m.estimate);
    if (p.qty !== undefined && p.unit && totalMoney.length >= 1 && p.percents.length >= 1) {
      if (group && p.indent <= group.indent) group = null;
      const name = p.name.replace(/^NP\s+/, "");
      const line: InboundLine = {
        line: lines.length + 1,
        text: name,
        ...quantityOf(name, p.qty, p.unit),
        qty: p.qty,
        unit: p.unit,
        amount: totalMoney.at(-1)!.cents,
        ...(totalMoney.length >= 2 ? { unitPrice: totalMoney[0]!.cents } : {}),
        vat: p.percents[0]!,
        ...(p.percents[1] !== undefined ? { discount: p.percents[1] } : {}),
        ...(p.origin ? { origin: p.origin, label: p.label, organic: true } : {}),
        ...(group ? { group: group.name } : {}),
      };
      lines.push(line);
    } else if (p.name && p.money.every((m) => m.estimate)) {
      // A box or an order over the goods below it.
      group = { name: p.name, indent: p.indent };
    } else if (p.name) notes.push(`Zeile nicht verstanden: „${r.text}“`);
  }

  for (const s of substitutions) {
    const line = lines.find((l) => l.text === s.to && !l.replaces);
    if (line) line.replaces = s.from;
    else notes.push(`${s.from} wurde gegen ${s.to} ausgetauscht, aber ${s.to} steht nicht auf dem Beleg.`);
  }
  // Everything Dirks delivers is organic („Alle Produkte stammen aus kontrolliert biologischer Erzeugung“).
  if (/kontrolliert biologischer Erzeugung/.test(all)) for (const l of lines) l.organic = true;
  const sum = lines.reduce((s, l) => s + (l.amount ?? 0), 0);
  if (goods !== undefined && sum !== goods) notes.push(`Die Positionen ergeben ${(sum / 100).toFixed(2).replace(".", ",")} €, der Beleg nennt ${(goods / 100).toFixed(2).replace(".", ",")} € Warenwert.`);
  if (lines.length === 0) notes.push("Auf dem Beleg wurden keine Waren gefunden.");

  const delivery = /Lieferdatum:\s*(\d{1,2}\.\d{1,2}\.\d{4})/.exec(all);
  return {
    channel: "pdf",
    format: "dirks-biokiste",
    kind,
    supplier: "Dirks Bio-Kiste",
    ...(ref ? { ref } : {}),
    key: `dirks-biokiste:${kind}:${ref ?? delivery?.[1] ?? "?"}`,
    ...(delivery ? { date: germanDate(delivery[1]!)! } : {}),
    ...(issued ? { issued } : {}),
    lines,
    ...(fee !== undefined ? { fee } : {}),
    ...(goods !== undefined ? { goods } : {}),
    ...(total !== undefined ? { total } : {}),
    notes,
  };
}

/** Converters for PDFs, tried in this order. */
export const PDF_CONVERTERS: readonly { format: string; label: string; matches: (rows: readonly PdfRow[]) => boolean; parse: (items: readonly PdfItem[]) => Inbound }[] = [
  { format: "dirks-biokiste", label: "Dirks Bio-Kiste", matches: isBiokiste, parse: parseBiokiste },
];

/** The Eingangsbeleg of a PDF, or null if no converter knows it. */
export function inboundFromPdf(items: readonly PdfItem[]): Inbound | null {
  const rows = pdfRows(items);
  const converter = PDF_CONVERTERS.find((c) => c.matches(rows));
  return converter ? converter.parse(items) : null;
}
